import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, Select } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import { filterOptions, REGISTRATION_CATEGORY_LIST, STATUS_LIST } from "./variables";
import useStoreFilters from "customHooks/useStoreFilters";
import { FILTER_PAGE, USER_TENANT_NAME } from "utils/constants";
import moment from "moment/moment";
import { uniq } from "lodash";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";

const { Option } = Select;

const useStyles = makeStyles({
  root: { width: "250px!important" },

  filtersContainer: {
    display: "flex",
    alignItems: "center",
    height: 60,
    overflow: "auto",

    "@media (max-width: 576px)": {
      height: "auto",
      flexDirection: "column",
      width: "100%",
      overflow: "hidden",
      gap: 10,
    },

    "& .ant-select-selection-overflow": {
      flexWrap: "nowrap",
    },
  },
  value: {
    width: "auto !important",
  },
  range: {
    width: "100px!important",
  },
});

const PricesFilter = ({ onFilter, priceType }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const { filters } = useStoreFilters(FILTER_PAGE.PRICES);
  const [activeFilter, setFilter] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);

  const {
    list: { depot, productTypes },
  } = useSelector(DictionariesSelector);

  const handleFilterChange = useCallback(
    value => {
      setFilter(value);
      form.setFieldsValue({
        type: value,
        value: null,
      });
    },
    [form]
  );

  useEffect(() => {
    const { pageSize, ...dataToSet } = filters;
    if (filters?.activateDateFrom) {
      setFilter("activationDate");
      form.setFieldsValue({
        type: "activationDate",
        value: [moment(filters?.activateDateFrom), moment(filters?.activateDateTo)],
      });
      return;
    }
    if (filters?.deactivateDateFrom) {
      setFilter("deactivationDate");
      form.setFieldsValue({
        type: "deactivationDate",
        value: [moment(filters?.deactivateDateFrom), moment(filters?.deactivateDateTo)],
      });
      return;
    }
    if (!!Object.keys(dataToSet)[0]) {
      setFilter(Object.keys(dataToSet)[0]);
      form.setFieldsValue({
        value: dataToSet[Object.keys(dataToSet)[0]],
        type: Object.keys(dataToSet)[0],
      });
    }
  }, [filters]);

  useEffect(() => {
    setFilter(null);
    form.setFieldsValue({
      type: null,
      value: null,
    });
  }, []);

  const onFinish = useCallback(
    values => {
      const normalizedFilters = !!filters?.pageSize ? { pageSize: filters.pageSize } : {};
      if (!(values.value && !values.type)) {
        onFilter({ ...normalizedFilters, [values.type]: values.value?.trim() });
      }
    },
    [activeFilter, filters, selectedRule]
  );

  return (
    <>
      <div className="custom-filter">
        <Form form={form} layout="inline" onFinish={onFinish}>
          <div className={classes.filtersContainer}>
            <Form.Item name="type">
              <Select
                style={{ width: "100%" }}
                tokenSeparators={[","]}
                size="default"
                placeholder="Select type"
                allowClear
                onChange={handleFilterChange}
              >
                {(filterOptions[priceType] || []).map(item => (
                  <Option key={item.value} value={item.value}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {!!activeFilter && <div className="filter-divider" />}
            {(activeFilter === "name" ||
              activeFilter === "priceId" ||
              activeFilter === "consumptionBand" ||
              activeFilter === "customerGroup") && (
              <Form.Item name="value" className={classes.value}>
                <Input placeholder={"Enter value"} />
              </Form.Item>
            )}
            {activeFilter === "status" && (
              <Form.Item name="value">
                <Select showSearch size="default" placeholder="Select status" allowClear>
                  {STATUS_LIST.map(item => (
                    <Option key={`${item}-option`} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {activeFilter === "product" && (
              <Form.Item name="value">
                <Select showSearch size="default" placeholder="Select type" allowClear>
                  {productTypes.map(item => (
                    <Option key={`${item}-option`} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {activeFilter === "registrationCategory" && (
              <Form.Item name="value">
                <Select showSearch size="default" placeholder="Select status" allowClear>
                  {REGISTRATION_CATEGORY_LIST.map(item => (
                    <Option key={`${item}-option`} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {activeFilter === "depot" && (
              <Form.Item name="value">
                <Select showSearch placeholder={"Select value"}>
                  {depot[USER_TENANT_NAME()] &&
                    uniq(depot[USER_TENANT_NAME()])
                      ?.sort()
                      ?.map(item => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                </Select>
              </Form.Item>
            )}
          </div>

          <CustomButton type="primary" htmlType="submit" text="Filter" size="small" />
        </Form>
      </div>
    </>
  );
};

export default PricesFilter;
